import React from "react";
import { useForm } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

const missingProvider = () => {
  throw new Error("DialogProvider is required.");
};
const DialogContext = React.createContext({
  confirm: missingProvider,
  prompt: missingProvider,
  alert: missingProvider,
  cancel: () => {},
});
export function useDialogs() {
  const context = React.useContext(DialogContext);
  const owner = React.useRef({}).current;
  React.useEffect(() => () => context.cancel(owner), [context, owner]);
  return React.useMemo(
    () => ({
      confirm: (message) => context.confirm(message, owner),
      prompt: (message) => context.prompt(message, owner),
      alert: (message) => context.alert(message, owner),
    }),
    [context, owner],
  );
}

function Question({ request, onClose }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { value: "" } });
  const isPrompt = request.kind === "prompt";
  const { ref: inputRef, ...nameField } = register("value", {
    validate: (value) => !isPrompt || !!value.trim() || "Enter a name.",
  });
  const submit = handleSubmit(({ value }) =>
    onClose(isPrompt ? value.trim() : true),
  );
  const titleId = React.useId();
  return (
    <Dialog open onClose={() => onClose(null)} aria-labelledby={titleId}>
      <DialogTitle id={titleId}>{request.message}</DialogTitle>
      <form onSubmit={submit}>
        {isPrompt && (
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Name"
              error={!!errors.value}
              helperText={errors.value?.message}
              {...nameField}
              inputRef={inputRef}
            />
          </DialogContent>
        )}
        <DialogActions>
          {request.kind !== "alert" && (
            <Button onClick={() => onClose(null)}>Cancel</Button>
          )}
          <Button type="submit" variant="contained">
            {isPrompt ? "Add" : request.kind === "alert" ? "OK" : "Confirm"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function DialogProvider({ children }) {
  const [request, setRequest] = React.useState(null);
  const pending = React.useRef(null);
  const close = React.useCallback((value) => {
    const resolve = pending.current;
    pending.current = null;
    setRequest(null);
    resolve?.resolve(value);
  }, []);
  React.useEffect(
    () => () => {
      pending.current?.resolve(null);
      pending.current = null;
    },
    [],
  );
  const dialogs = React.useMemo(() => {
    const ask = (kind) => (message, owner) => {
      // A second click must not create a second confirmation or mutation.
      if (pending.current) return Promise.resolve(null);
      return new Promise((resolve) => {
        pending.current = { resolve, owner };
        setRequest({ kind, message });
      });
    };
    return {
      confirm: ask("confirm"),
      prompt: ask("prompt"),
      alert: ask("alert"),
      cancel: (owner) => {
        if (pending.current?.owner === owner) close(null);
      },
    };
  }, [close]);
  return (
    <DialogContext.Provider value={dialogs}>
      {children}
      {request && <Question request={request} onClose={close} />}
    </DialogContext.Provider>
  );
}
